import { createContext, useContext, useReducer, useEffect, useCallback, type ReactNode } from "react";

// --- Types ---

export interface CartItem {
  cartKey: string;
  productId: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  price: number;
  imageUrl: string;
  quantity: number;
  maxStock: number;
  selectedVariants: Record<string, string>;
}

interface CartState {
  items: CartItem[];
  savedItems: CartItem[];
  lastAdded: string | null;
}

type CartAction =
  | { type: "ADD_ITEM"; payload: Omit<CartItem, "cartKey"> }
  | { type: "REMOVE_ITEM"; payload: string }
  | { type: "UPDATE_QUANTITY"; payload: { cartKey: string; quantity: number } }
  | { type: "CLEAR_CART" }
  | { type: "SAVE_FOR_LATER"; payload: string }
  | { type: "MOVE_TO_CART"; payload: string }
  | { type: "CLEAR_SAVED" }
  | { type: "DISMISS_TOAST" };

interface CartContextValue {
  items: CartItem[];
  savedItems: CartItem[];
  itemCount: number;
  subtotal: number;
  lastAdded: string | null;
  addItem: (item: Omit<CartItem, "cartKey">) => void;
  removeItem: (cartKey: string) => void;
  updateQuantity: (cartKey: string, quantity: number) => void;
  clearCart: () => void;
  saveForLater: (cartKey: string) => void;
  moveToCart: (cartKey: string) => void;
  clearSaved: () => void;
  dismissToast: () => void;
}

// --- Helpers ---

const STORAGE_KEY = "generalstore_cart";
const SAVED_STORAGE_KEY = "generalstore_saved";

export function makeCartKey(productId: string, variants: Record<string, string>): string {
  const variantStr = Object.entries(variants)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join("|");
  return `${productId}__${variantStr}`;
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage full or unavailable — silently ignore
  }
}

function loadSaved(): CartItem[] {
  try {
    const raw = localStorage.getItem(SAVED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function saveSaved(items: CartItem[]): void {
  try {
    localStorage.setItem(SAVED_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage full or unavailable — silently ignore
  }
}

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD_ITEM": {
      const key = makeCartKey(action.payload.productId, action.payload.selectedVariants);
      const existingIdx = state.items.findIndex((i) => i.cartKey === key);

      let nextItems: CartItem[];
      if (existingIdx >= 0) {
        nextItems = state.items.map((item, idx) =>
          idx === existingIdx
            ? { ...item, quantity: Math.min(item.maxStock, item.quantity + action.payload.quantity) }
            : item
        );
      } else {
        nextItems = [...state.items, { ...action.payload, cartKey: key }];
      }

      saveCart(nextItems);
      return { items: nextItems, lastAdded: action.payload.nameEn };
    }

    case "REMOVE_ITEM": {
      const nextItems = state.items.filter((i) => i.cartKey !== action.payload);
      saveCart(nextItems);
      return { ...state, items: nextItems };
    }

    case "UPDATE_QUANTITY": {
      if (action.payload.quantity < 1) return state;
      const nextItems = state.items.map((item) =>
        item.cartKey === action.payload.cartKey
          ? { ...item, quantity: Math.min(item.maxStock, action.payload.quantity) }
          : item
      );
      saveCart(nextItems);
      return { ...state, items: nextItems };
    }

    case "CLEAR_CART": {
      saveCart([]);
      return { ...state, items: [], lastAdded: null };
    }

    case "SAVE_FOR_LATER": {
      const itemToSave = state.items.find((i) => i.cartKey === action.payload);
      if (!itemToSave) return state;
      const nextCartItems = state.items.filter((i) => i.cartKey !== action.payload);
      const nextSavedItems = [...state.savedItems, itemToSave];
      saveCart(nextCartItems);
      saveSaved(nextSavedItems);
      return { ...state, items: nextCartItems, savedItems: nextSavedItems };
    }

    case "MOVE_TO_CART": {
      const itemToMove = state.savedItems.find((i) => i.cartKey === action.payload);
      if (!itemToMove) return state;
      const nextSavedItems = state.savedItems.filter((i) => i.cartKey !== action.payload);
      const existingIdx = state.items.findIndex((i) => i.cartKey === action.payload);
      let nextCartItems: CartItem[];
      if (existingIdx >= 0) {
        nextCartItems = state.items.map((item, idx) =>
          idx === existingIdx
            ? { ...item, quantity: Math.min(item.maxStock, item.quantity + itemToMove.quantity) }
            : item
        );
      } else {
        nextCartItems = [...state.items, itemToMove];
      }
      saveCart(nextCartItems);
      saveSaved(nextSavedItems);
      return { ...state, items: nextCartItems, savedItems: nextSavedItems };
    }

    case "CLEAR_SAVED": {
      saveSaved([]);
      return { ...state, savedItems: [] };
    }

    case "DISMISS_TOAST":
      return { ...state, lastAdded: null };

    default:
      return state;
  }
}

// --- Context ---

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, { items: loadCart(), savedItems: loadSaved(), lastAdded: null });

  const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = state.items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const addItem = useCallback((item: Omit<CartItem, "cartKey">) => {
    dispatch({ type: "ADD_ITEM", payload: item });
  }, []);

  const removeItem = useCallback((cartKey: string) => {
    dispatch({ type: "REMOVE_ITEM", payload: cartKey });
  }, []);

  const updateQuantity = useCallback((cartKey: string, quantity: number) => {
    dispatch({ type: "UPDATE_QUANTITY", payload: { cartKey, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR_CART" });
  }, []);

  const saveForLater = useCallback((cartKey: string) => {
    dispatch({ type: "SAVE_FOR_LATER", payload: cartKey });
  }, []);

  const moveToCart = useCallback((cartKey: string) => {
    dispatch({ type: "MOVE_TO_CART", payload: cartKey });
  }, []);

  const clearSaved = useCallback(() => {
    dispatch({ type: "CLEAR_SAVED" });
  }, []);

  const dismissToast = useCallback(() => {
    dispatch({ type: "DISMISS_TOAST" });
  }, []);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (!state.lastAdded) return;
    const timer = setTimeout(() => dispatch({ type: "DISMISS_TOAST" }), 3000);
    return () => clearTimeout(timer);
  }, [state.lastAdded]);

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        savedItems: state.savedItems,
        itemCount,
        subtotal,
        lastAdded: state.lastAdded,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        saveForLater,
        moveToCart,
        clearSaved,
        dismissToast,
      }}
    >
      {children}
      {/* Toast notification */}
      {state.lastAdded && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="flex items-center gap-3 px-5 py-3.5 bg-foreground-950 text-white rounded-xl shadow-lg">
            <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-primary-400"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-white">Added to cart</p>
              <p className="text-xs text-white/60 truncate max-w-48">{state.lastAdded}</p>
            </div>
            <button
              onClick={dismissToast}
              className="ml-2 w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white/80 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside <CartProvider>");
  }
  return ctx;
}